import { useEffect, useState } from 'react'
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { apiGet } from '../../api'

interface Point {
  ts: number
  total_cents: number
  subtotal_cents: number
  groups: Record<string, number>
}
interface HistoryResponse { points: Point[] }

interface Props {
  hours?: number
  height?: number
  compact?: boolean   // hides axes/tooltip — for KPI sparkline
}

export default function CostChart({ hours = 720, height = 220, compact = false }: Props) {
  const [data, setData] = useState<Point[]>([])

  useEffect(() => {
    let alive = true
    apiGet<HistoryResponse>(`/infra/cost/history?hours=${hours}`)
      .then(r => { if (alive) setData(r.points) })
      .catch(() => undefined)
    return () => { alive = false }
  }, [hours])

  if (data.length === 0 && compact) {
    return <div style={{ height, opacity: 0.3, fontSize: '0.65rem', color: 'var(--text-muted)' }}>collecting data…</div>
  }
  if (data.length === 0) {
    return (
      <div style={{ height, display: 'grid', placeItems: 'center',
                    color: 'var(--text-muted)', fontSize: '0.8rem' }}>
        Cost timeline empty — chart fills as the cron records hourly snapshots.
      </div>
    )
  }

  const formatTime = (t: number) => {
    const d = new Date(t)
    return hours <= 48
      ? `${d.getHours().toString().padStart(2,'0')}:00`
      : `${d.getDate()}/${d.getMonth()+1}`
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: compact ? -32 : 0, bottom: 0 }}>
        <defs>
          <linearGradient id="costFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#5af2ff" stopOpacity={0.55}/>
            <stop offset="100%" stopColor="#5af2ff" stopOpacity={0.02}/>
          </linearGradient>
        </defs>
        {!compact && (
          <XAxis dataKey="ts" tickFormatter={formatTime} stroke="#6b7795"
                 tick={{ fontSize: 10 }} minTickGap={32} />
        )}
        {!compact && (
          <YAxis tickFormatter={(v) => `$${(v/100).toFixed(0)}`} stroke="#6b7795"
                 tick={{ fontSize: 10 }} width={42} />
        )}
        {!compact && (
          <Tooltip
            contentStyle={{ background: '#0f1320', border: '1px solid #2a3148', borderRadius: 6, fontSize: 12 }}
            labelFormatter={(t) => new Date(t as number).toLocaleString('en-GB')}
            formatter={(v) => [`$${(Number(v)/100).toFixed(2)}`, 'Total']}
          />
        )}
        <Area type="monotone" dataKey="total_cents" stroke="#5af2ff" strokeWidth={2}
              fill="url(#costFill)" isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  )
}
